<template>
  <div class="d-none"></div>
</template>

<script>
import { getDatabase, ref, onValue, remove, off, set, onDisconnect } from "firebase/database";
import { getTareaByID } from '@/helpers/api/tasks'
import { getRdiByID } from '@/helpers/api/rdi'
import EventBus from '@/helpers/event_bus'

export default {
  name: 'FirebaseController',

  computed: {
    selectedProject() {
      return this.$store.projectsStore.state.selectedProject
    },
    userProjects() {
      return this.$store.projectsStore.state.projectsList
    },
    userInfo() {
      return this.$store.userStore.state.userInfo
    },
  },

  watch: {
    userProjects(newProjects) {
      if (newProjects && this.userInfo) {
        this.syncListeners(newProjects)
      }
    },
    userInfo(newUserInfo) {
      if (newUserInfo && this.userProjects) {
        this.syncListeners(this.userProjects)
      }
    }
  },

  data: () => ({
    activeListeners: {},
    presenceRefs: {},
  }),

  mounted() {
    if (this.userProjects && this.userInfo) {
      this.syncListeners(this.userProjects)
    }
  },

  beforeDestroy() {
    this.removeAllListeners()
  },

  methods: {
    playNotification() {
      try {
        const audio = new Audio(require('@/assets/Sounds/notification.mp3'))
        audio.play()
      } catch (error) {
        console.error('Error playing notification sound:', error)
      }
    },

    syncListeners(projects) {
      const db = getDatabase()
      const userId = this.userInfo.id
      const incomingIds = new Set(projects.map(p => p.id))

      Object.keys(this.activeListeners).forEach(projectId => {
        if (!incomingIds.has(projectId)) {
          this.removeListener(projectId)
        }
      })

      projects.forEach(project => {
        const caps = project.user_project?.capabilities ?? {}
        const hasAnyCapability = (
          caps.view_tasks_listing ||
          caps.view_rdi ||
          caps.view_additionals ||
          caps.view_communication_books
        )

        if (!hasAnyCapability) return
        if (this.activeListeners[project.id]) return

        const projectRef = ref(db, `${project.id}/${userId}`)

        const unsubscribe = onValue(projectRef, (snapshot) => {
          const data = snapshot.val()
          if (!data) return

          const makeSound = project.useroptions?.notification_sound ?? false

          if (caps.view_tasks_listing && data.task) {
            this.handleTaskNotification(data.task, project, makeSound)
            remove(ref(db, `${project.id}/${userId}/task`))
          }

          if (caps.view_rdi && data.rdi) {
            this.handleRdiNotification(data.rdi, project, makeSound)
            remove(ref(db, `${project.id}/${userId}/rdi`))
          }

          if (caps.view_additionals && data.additional) {
            this.handleAdditionalNotification(data.additional, project, makeSound)
            remove(ref(db, `${project.id}/${userId}/additional`))
          }

          if (caps.view_communication_books && data.communication_folio) {
            this.handleCommunicationFolioNotification(data.communication_folio, project, makeSound)
            remove(ref(db, `${project.id}/${userId}/communication_folio`))
          }

					if(data.simple_notification){
						if(data.simple_notification?.action == 'capabilities_updated'){
							if(this.userInfo?.id && this.selectedProject?.id){
								this.$store.projectsStore.dispatch('getSelectedProject')
								const notification_data = {
									message: 'Se han actualizado tus permisos en el proyecto. Actualizando...',
									title: 'Permisos actualizados',
									created: new Date(),
									type: 'warning',
									icon: 'mdi-check-circle',
									color: '#fb8c00'
								}
								this.$store.globalStore.dispatch('addMessage', notification_data)
							}
						}
						if(data.simple_notification?.action == 'file_to_forge_completed'){
              const fileName = data.simple_notification?.file_name
              this.$store.globalStore.commit('removeForgeProcessingFile', {
                file_id: data.simple_notification?.file_id,
                file_name: fileName
              })
							if(this.userInfo?.id && this.selectedProject?.id){
								EventBus.$emit('completedFileUpload')
								const notification_data = {
                  message: fileName ? `Ya puedes visualizar ${fileName}.` : 'Ya puedes visualizar tu modelo en el visor de Autodesk',
                  title: fileName ? `Archivo Disponible` : 'Archivo Disponible en Visor de Autodesk',
									created: new Date(),
									type: 'success',
									icon: 'mdi-check-circle',
									color: '#3EBFA3'
								}
								this.$store.globalStore.dispatch('addMessage', notification_data)
							}
						}
						if(data.simple_notification?.action == 'project_removal'){
							if(this.userInfo?.id && this.selectedProject?.id){
								setTimeout(() => {
									window.location.reload()
								}, 3000)
								const notification_data = {
									message: 'Te han quitado el acceso a este proyecto. La página se recargará automáticamente.',
									title: 'Sin Acceso al Proyecto',
									created: new Date(),
									type: 'error',
									icon: 'mdi-alert-circle',
									color: '#fb8c00'
								}
								this.$store.globalStore.dispatch('addMessage', notification_data)
							}
						}
						if(data.simple_notification?.action == 'project_job_updated'){
							if(this.userInfo?.id && this.selectedProject?.id){
								this.$store.projectsStore.dispatch('getSelectedProject')
								const notification_data = {
									message: 'Ha cambiado tu rol en el proyecto.',
									title: 'Rol Actualizado',
									created: new Date(),
									type: 'warning',
									icon: 'mdi-check-circle',
									color: '#fb8c00'
								}
								this.$store.globalStore.dispatch('addMessage', notification_data)
							}
						}
						if(data.simple_notification?.action == 'project_modules_updated'){
							if(this.userInfo?.id && this.selectedProject?.id){
								console.log('project_modules_updated notification received, refreshing selected project')
								this.$store.projectsStore.dispatch('getSelectedProject')
								const notification_data = {
									message: 'Se han actualizado los módulos del proyecto.',
									title: 'Módulos Actualizados',
									created: new Date(),
									type: 'warning',
									icon: 'mdi-check-circle',
									color: '#fb8c00'
								}
								this.$store.globalStore.dispatch('addMessage', notification_data)
							}
						}
						if(data.simple_notification?.action == 'chat_room_message'){
							const notification_data = {
								message: data.simple_notification?.message || 'Nuevo mensaje',
								title: data.simple_notification?.user_name ? `Mensaje de Chat: ${data.simple_notification?.user_name}` : 'Nuevo Mensaje',
								created: new Date(),
								type: 'success',
								icon: 'mdi-forum-outline',
								color: '#fb8c00',
								close: 8000,
								action: 'chat_room_message',
								chat_room_id: data.simple_notification?.chat_room_id,
							}
							this.$store.globalStore.dispatch('addMessage', notification_data)
							EventBus.$emit('reloadChatRoom', data.simple_notification)
						}
						if(data.simple_notification?.action == 'meeting_element_update'){
							EventBus.$emit('onMeetingElementsChange', data.simple_notification)
						}
						if(data.simple_notification?.action == 'project_elements_updated' && this.selectedProject?.id == project.id){
							EventBus.$emit('onProjectElementsChange')
						}
            remove(ref(db, `${project.id}/${userId}/simple_notification`))
					}
        })

        this.activeListeners[project.id] = { ref: projectRef, unsubscribe }
        // also set presence for this project so user is visible across the app
        this.startPresenceForProject(project)
      })
    },

    removeListener(projectId) {
      const listener = this.activeListeners[projectId]
      if (!listener) return
      off(listener.ref)
      listener.unsubscribe?.()
      // remove presence entry for this project
      try { this.removePresenceForProject(projectId) } catch (e) { void e }
      delete this.activeListeners[projectId]
    },

    removeAllListeners() {
      Object.keys(this.activeListeners).forEach(id => this.removeListener(id))
    },

    startPresenceForProject(project) {
      try {
        if (!this.userInfo || !this.userInfo.id) return
        const db = getDatabase()
        const userId = this.userInfo.id
        const projectId = project.id

        // new canonical path
        const newPath = `${projectId}/chats/${userId}`
        const presenceRefNew = ref(db, newPath)
        set(presenceRefNew, {
          name: this.userInfo.name,
          user_id: userId,
          status: 'online',
          last_seen: new Date().toISOString()
        })
        const onDiscNew = onDisconnect(presenceRefNew)
        onDiscNew.remove()

        // optional: keep old path during migration
        let presenceRefOld = null
        let onDiscOld = null
        try {
          const oldPath = `${projectId}/${userId}`
          presenceRefOld = ref(db, oldPath)
          set(presenceRefOld, {
            name: this.userInfo.name,
            user_id: userId,
            status: 'online',
            last_seen: new Date().toISOString()
          })
          onDiscOld = onDisconnect(presenceRefOld)
          onDiscOld.remove()
        } catch (e) {
          void e
        }

        this.presenceRefs = this.presenceRefs || {}
        this.presenceRefs[projectId] = {
          presenceRefNew,
          onDiscNew,
          presenceRefOld,
          onDiscOld
        }
      } catch (e) {
        console.error('startPresenceForProject error', e)
      }
    },

    removePresenceForProject(projectId) {
      try {
        const refs = this.presenceRefs && this.presenceRefs[projectId]
        if (!refs) return
        try { refs.onDiscNew?.cancel && refs.onDiscNew.cancel() } catch (e) { void e }
        try { remove(refs.presenceRefNew) } catch (e) { void e }
        try { refs.onDiscOld?.cancel && refs.onDiscOld.cancel() } catch (e) { void e }
        try { remove(refs.presenceRefOld) } catch (e) { void e }
        delete this.presenceRefs[projectId]
      } catch (e) {
        void e
      }
    },

    showNotifications(data, makeSound) {
      Object.values(data).forEach(notification => {
        if (notification.created_by === this.userInfo.id) return
        this.$store.globalStore.dispatch('addMessage', notification)
        if (makeSound) this.playNotification()
      })
    },

    isSelectedProject(projectId) {
      return projectId === this.$store.projectsStore.state.selectedProject?.id
    },

    refreshNotifications(projectId) {
      this.$store.notificationsStore.dispatch('getNotifications', { project_id: projectId })
    },

    handleTaskNotification(taskData, project, makeSound) {
      this.showNotifications(taskData, makeSound)

      if (this.isSelectedProject(project.id)) {
				if(taskData.action === 3 || taskData.action === 0){
					EventBus.$emit('onProjectElementsChange')
				}

        this.refreshNotifications(project.id)
        this.getConsultasList(project.id)
      }

      const currentTaskId = this.$store.consultasStore.state.currentTarea?.id
      if (currentTaskId && taskData[currentTaskId]) {
        this.getTaskById(currentTaskId)
      }
    },

    handleRdiNotification(rdiData, project, makeSound) {
      this.showNotifications(rdiData, makeSound)

      if (this.isSelectedProject(project.id)) {
				if(rdiData.action === 3 || rdiData.action === 0){
					EventBus.$emit('onProjectElementsChange')
				}

        this.refreshNotifications(project.id)
        this.getRDIList(project.id)
      }

      const currentRdiId = this.$store.rdiStore.state.currentRDI?.id
      if (currentRdiId && rdiData[currentRdiId]) {
        this.getRDIById(currentRdiId)
      }
    },

    handleAdditionalNotification(additionalData, project, makeSound) {
      this.showNotifications(additionalData, makeSound)

      if (this.isSelectedProject(project.id)) {
				if(additionalData.action === 3 || additionalData.action === 0){
					EventBus.$emit('onProjectElementsChange')
				}

        this.refreshNotifications(project.id)
				if(this.$route.name === 'Adicionales' && this.$route.query.listing_type == 3){
					EventBus.$emit('reloadInformativeTable')
				}
				else{
					this.getAdditionalList(project.id)
				}
      }

      const currentAdditionalId = this.$store.adicionalesStore.state.currentAdicional?.id
      if (currentAdditionalId && additionalData[currentAdditionalId]) {
        this.$store.adicionalesStore.dispatch('getAdditionalByID', {
          additional_id: currentAdditionalId,
          seen_by_me: true,
          dontSetLoading: true,
        })
      }
    },

    handleCommunicationFolioNotification(folioData, project, makeSound) {
      this.showNotifications(folioData, makeSound)

      if (!this.isSelectedProject(project.id)) return

      this.refreshNotifications(project.id)
      const communicationStore = this.$store.communicationBooksStore
      if (communicationStore.state.selectedBookId) {
        communicationStore.dispatch('loadFolios', {
          bookId: communicationStore.state.selectedBookId,
          search: communicationStore.state.search,
        })
      }

      const currentFolioId = communicationStore.state.currentFolio?.id
      if (currentFolioId && folioData[currentFolioId]) {
        communicationStore.dispatch('loadFolio', currentFolioId)
      }
    },

    async getTaskById(task_id) {
      const { code, task } = await getTareaByID({ task_id })
      if (code === 200) {
        this.$store.consultasStore.commit('setCurrentTarea', task)
      }
    },

    getConsultasList(project_id) {
      if (project_id) this.$store.consultasStore.dispatch('getMyTasks', { project_id })
    },

    async getRDIById(rdi_id) {
      const { code, rdi } = await getRdiByID({ rdi_id })
      if (code === 200) {
        this.$store.rdiStore.commit('setCurrentRDI', rdi)
      }
    },

    getRDIList(project_id) {
      if (project_id) this.$store.rdiStore.dispatch('getRDI', { project_id })
    },

    getAdditionalList(project_id) {
      if (project_id) this.$store.adicionalesStore.dispatch('getAdditionals', { project_id })
    },
  },
}
</script>